import React, { useContext, useState } from "react";

import { mdiDelete, mdiTrashCanOutline } from "@mdi/js";
import { Icon } from "@mdi/react";
import { useTranslation } from "react-i18next";

import CreateNewChildMedia from "./CreateNewChildMedia";
import MasterMediaContext from "../../../contexts/MasterMediaContext";
import ConfirmModal from "../../modal/ConfirmModal";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";

import type { JSX } from "react";

import type {
  ChildMedia,
  MasterMediaContextValues,
} from "../../../contexts/MasterMediaContext";
import type { ModalActions } from "../../modal/Modal";

interface VerticalTabsProps {
  children: React.ReactNode;
}

interface TabDeleteButtonProps {
  onClickDelete: () => void;
  isSelected: boolean;
}

interface TabLabelProps {
  label: string;
  classificationLabel: string;
}

function TabDeleteButton({
  onClickDelete,
  isSelected,
}: TabDeleteButtonProps): JSX.Element | null {
  if (!isSelected) return null;

  return (
    <div className="flex">
      <i className="material-icons" onClick={onClickDelete}>
        <Icon path={mdiDelete} size={0.9} />
      </i>
    </div>
  );
}

function TabLabel({ label, classificationLabel }: TabLabelProps): JSX.Element {
  const hasTitle = label !== classificationLabel;
  return (
    <div className="flex flex-col items-start overflow-hidden">
      <div className="w-full overflow-hidden text-sm text-ellipsis whitespace-nowrap">
        {label}
      </div>
      <div className="text-xs font-normal">
        {hasTitle ? classificationLabel : ""}
      </div>
    </div>
  );
}

export default function VerticalTabs({
  children,
}: VerticalTabsProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const [contentToDelete, setContentToDelete] = useState<{
    uuid: string;
    label: string;
  } | null>(null);

  const { activeTab, setActiveTab, childMediaList, deleteChildMedia } =
    useContext<MasterMediaContextValues>(MasterMediaContext);

  const modalActions: ModalActions[] = [
    {
      label: t("modal:cancel"),
      onClick: () => setContentToDelete(null),
      buttonVariant: "secondary",
    },
    {
      label: t("modal:confirm"),
      onClick: async () =>
        contentToDelete && deleteChildMedia(contentToDelete.uuid),
      buttonVariant: "delete",
    },
  ];

  /**
   * If the active tab is not in the list of child media, we set the active tab to the last child media
   * This is to prevent the active tab from being temporarily still set to temp_id after a child media is created
   */

  let tabValue = activeTab;

  if (
    childMediaList.find((media: ChildMedia) => media.uuid === activeTab) ===
    undefined
  ) {
    tabValue = childMediaList[childMediaList.length - 1]?.uuid ?? activeTab;
  }

  return (
    <div className="flex max-lg:flex-col max-lg:gap-4">
      <Tabs
        onValueChange={setActiveTab}
        orientation="vertical"
        value={tabValue}
      >
        <TabsList variant="nav">
          {childMediaList.map((media) => (
            <TabsTrigger
              key={media.uuid}
              className="relative max-w-[200px] items-center justify-between!"
              value={media.uuid}
            >
              <TabLabel
                classificationLabel={media.classificationLabel}
                label={media.label}
              />
              <TabDeleteButton
                isSelected={tabValue === media.uuid}
                onClickDelete={() =>
                  setContentToDelete({ uuid: media.uuid, label: media.label })
                }
              />
            </TabsTrigger>
          ))}
          <CreateNewChildMedia />
        </TabsList>
      </Tabs>
      {tabValue ? children : null}
      <ConfirmModal
        actions={modalActions}
        iconPath={mdiTrashCanOutline}
        isOpen={Boolean(contentToDelete)}
        onClose={(): void => setContentToDelete(null)}
        size="md"
        title={t("deleteTitle")}
      >
        {t("deleteBody", { content: contentToDelete?.label })}
      </ConfirmModal>
    </div>
  );
}
